import React, {useContext} from 'react';
import {ReactReduxContext, useDispatch, useSelector} from 'react-redux';
import {setCurrentStep, setError} from '../../actions';
import HelpText from '../helptext';
import StartOverButton from '../startOverButton';
import FormStep, {ERROR_LIST} from '.';
import AutoHeader from '../autoHeader';

import {
    FAMILY_SIZE_HELP_TEXT,
    FINANCIAL_DETAILS_HELP_TEXT,
    OTHER_INCOME_TEXT,
    PARENT_ADJUSTED_INCOME_HELP_TEXT,
    PARENT_ASSETS_HELP_TEXT,
    PARENT_TAXES_HELP_TEXT,
    STUDENT_ADJUSTED_INCOME_HELP_TEXT,
    STUDENT_ASSETS_HELP_TEXT,
    STUDENT_ASSETS_INDEPENDENT_TEXT_MARRIED,
    STUDENT_ASSETS_INDEPENDENT_TEXT_SINGLE,
    STUDENT_SPOUSE_COMBINED_INCOME_HELP_TEXT,
    STUDENT_SPOUSE_TAXES_HELP_TEXT,
    STUDENT_TAXES_HELP_TEXT
} from './bi/constants/INFO_TEXT';

import {
    HAS_DEPENDENTS_NO,
    HAS_DEPENDENTS_YES,
    PARENT_MARITAL_STATUS_MARRIED, PARENT_MARITAL_STATUS_SINGLE,
    RESIDENCY_CALIFORNIA,
    STATUS_DEPENDENT,
    STATUS_INDEPENDENT,
    STUDENT_MARITAL_STATUS_MARRIED, STUDENT_MARITAL_STATUS_SINGLE
} from "./bi/constants/MAGIC_NUMBERS";

import {Step3Input} from "../step3Input";
import {ErrorPanel} from "../errorPanel";
import {Step3Radio} from "../step3Radio";
import {Step3DropDown} from "../step3DropDown";
import {StepIndicator} from "../stepIndicator";

const Step3 = () => {

    const dispatch = useDispatch();
    const context = useContext(ReactReduxContext);
    const formData = context.store.getState().app.stepData;

    const isMarried = useSelector((state) =>
       state.app.stepData.step3.studentMaritalStatus === STUDENT_MARITAL_STATUS_MARRIED
    );

    const isIndependent = formData.step1.status ===  STATUS_INDEPENDENT;

    const residentHeading = (parseInt(formData.step2.residency) === RESIDENCY_CALIFORNIA)
        ? "California Resident"
        : "Nonresident";

    return (
        <FormStep>
            <AutoHeader text="Step 3" />
            <StepIndicator title="Previous Step" previous={true} prevStep={2}/>

            <div className="section-3 fae-section-content">

                <ErrorPanel />

                <div className="help-title-container">
                  <h2 className="info-title">{residentHeading}</h2>
                </div>

                <div id="step-3-campus-housing">
                    <Step3DropDown elementName="campus" dataSource="campusList" labelText="Selected Campus" />
                    <Step3DropDown elementName="housing" dataSource="housingList" labelText="Housing Type" />
                </div>

                <h2>Personal Details</h2>

                {/* Dependent */}
                {parseInt(formData.step1.status) === STATUS_DEPENDENT ? (
                    <RadioParentMaritalStatus />
                ) : (
                    // Independent
                    <>
                        <Step3Input labelText="Student Age" record="studentAge"/>
                        <RadioStudentMaritalStatus />
                        <RadioStudentHasDependents />
                    </>
                )}

                <Step3Input
                  labelText="Family Size"
                  infoText={FAMILY_SIZE_HELP_TEXT}
                  infoName="family-size"
                  record="familySize"
                />

                <div className="help-title-container">
                  <h2 className="info-title">Financial Details</h2>
                  <HelpText name="financial-details" text={FINANCIAL_DETAILS_HELP_TEXT} />
                </div>

                {parseInt(formData.step1.status) === 1 ? (
                    // Dependent
                    <>
                        <RadioParentIncomeEarnedBy />

                        <Step3Input
                          labelText="Parent(s) Adjusted Gross Income"
                          infoText={PARENT_ADJUSTED_INCOME_HELP_TEXT}
                          infoName="parent-income"
                          record="parentAdjustedGrossIncome"
                        />

                        <Step3Input
                            labelText="Parent(s) Other Income"
                            infoText={OTHER_INCOME_TEXT}
                            record="parentOtherIncome"/>

                        <Step3Input
                          labelText="Parent(s) Taxes Paid"
                          infoText={PARENT_TAXES_HELP_TEXT}
                          infoName="parent-taxes"
                          record="parentTaxesPaid"
                        />

                        <Step3Input
                          labelText="Parent(s) Assets"
                          infoText={PARENT_ASSETS_HELP_TEXT}
                          infoName="parent-assets"
                          record="parentAssets"
                        />
                    </>
                    ) : (
                    <>
                        {/* Independent */}
                        <RadioStudentIncomeEarnedBy/>
                    </>
                    )
                }

                <Step3Input
                  labelText="Student Adjusted Gross Income"
                  infoText={isMarried ? STUDENT_SPOUSE_COMBINED_INCOME_HELP_TEXT
                                      : STUDENT_ADJUSTED_INCOME_HELP_TEXT}
                  infoName="student-income"
                  record="studentAdjustedGrossIncome"
                />
                <Step3Input
                    labelText="Student Other Income"
                    infoText={OTHER_INCOME_TEXT}
                    record="studentOtherIncome"/>

                <Step3Input
                  labelText="Student Taxes Paid"
                  infoText={isMarried ? STUDENT_SPOUSE_TAXES_HELP_TEXT
                                      : STUDENT_TAXES_HELP_TEXT}
                  infoName="student-taxes"
                  record="studentTaxesPaid"
                />

                <Step3Input
                  labelText="Student Assets"
                  infoText={
                    isIndependent
                        ? isMarried
                            ? STUDENT_ASSETS_INDEPENDENT_TEXT_MARRIED
                            : STUDENT_ASSETS_INDEPENDENT_TEXT_SINGLE
                        : STUDENT_ASSETS_HELP_TEXT
                    }
                  infoName="student-assets"
                  record="studentAssets"
                />

                <div style={{display: "flex", flexDirection: "row"}}>
                    <div style={{marginRight: "1rem"}}>
                        <button onClick={(e) => {
                            e.preventDefault();
                            dispatch(Step3CalculateThunk());
                        }}>Calculate</button>
                    </div>
                </div>

            </div>
        </FormStep>
    )
};


/**
 * Checks if the provided test data meets certain criteria.
 *
 * @param {number} testIndependent - The value indicating whether the student is independent or not.
 *                                   2 for independent, otherwise not independent.
 * @param {object} testData - The test data to be checked.
 * @returns {object} - An object containing the errors found in the test data.
 */
function Step3Check(testIndependent, testData, fullData) {
    //  Toggle rather than rewrite code
    const ALLOW_EMPTY = true;

    const independent = testIndependent === STATUS_INDEPENDENT;

    //  Add field values that should be considered to have a value of 0
    //  if they're empty or undefined.
    const empty_is_zero = [];
    let field_check;

    //  These are input fields that will be tested to be 0 or greater with actual content.
    if(independent) {
        field_check = [
            'studentAge', 'studentMaritalStatus', 'studentHasDependents',
            'studentIncomeEarnedBy', 'studentAdjustedGrossIncome',
            'studentOtherIncome', 'studentTaxesPaid', 'studentAssets'
        ];
    } else {
        field_check = [
            'parentMaritalStatus', 'parentIncomeEarnedBy',
            'parentAdjustedGrossIncome', 'parentOtherIncome', 'parentTaxesPaid',
            'parentAssets', 'studentAdjustedGrossIncome', 'studentOtherIncome',
            'studentTaxesPaid', 'studentAssets'
        ];
    }

    //  Input fields that require a more complex test beyond being 0 or empty.
    let detailed_check = ['familySize'];
    let return_errors = {};

    //  This is an example hook to reduce code in this overall check
    //  for individual field tests that may come up in the future.
    let checks = detailInputCheck(fullData);
    for(const k of detailed_check) {
        let current_test = checks[k]();
        if(current_test && current_test !== "") {
            return_errors[k] = current_test;
        }
    }

    //  This is a basic check that a text field has a number.
    //  There's also a test to allow empty values in text fields to represent 0.
    //  There's no tests for the following:
    //      * Proper amount of digits past the decimal point
    //      * Ignoring currency symbols (dollar, euro, yen, etc.)
    //      * Ignoring any thousand separators (',', '.', ' ', '_' to name a few)
    //  Fields that aren't in the 'empty_is_zero' array are going to be radio buttons,
    //  and those are one based.  A zero in those will be considered an error.
    for(const k of field_check) {
        let current = undefined;
        let val = testData[k];
        if(
            ((empty_is_zero.includes(k) && ALLOW_EMPTY) && (val === undefined || val.trim() === '')) ||
            (parseInt(val) === 0)) {
                current = 0;
        } else if(!isNaN(parseFloat(val)) && isFinite(val) && parseInt(val) > 0) {
            current = val;
        }

        if(current === undefined) {
            return_errors[k] = ERROR_LIST[k].errorText;
        }
    }

    return return_errors;
}

/**
 * Represents a component for selecting parent's marital status.
 * @returns {React.Component} - The RadioParentMaritalStatus component.
 */
const RadioParentMaritalStatus = () => {
    return(
        <Step3Radio
            headerText="Parent's Marital Status"
            radioName="parentMaritalStatus"
            radioValues={
                [
                    {id: "parent-marital-single", text: "Single"},
                    {id: "parent-marital-married", text: "Married"}
                ]
            }
            fieldName="parentMaritalStatus"
        />
    )
}

/**
 * Represents a component for selecting the number of parents providing income
 * @returns {React.Component} - The RadioParentIncomeEarnedBy component.
 */
const RadioParentIncomeEarnedBy = () => {
    return(
        <Step3Radio
            headerText="Parent's Income Earned By"
            radioName="parentIncomeEarnedBy"
            radioValues={
                [
                    {id: "parent-income-neither", text: "Neither"},
                    {id: "parent-income-one", text: "One"},
                    {id: "parent-income-both", text: "Both"}
                ]}
            fieldName="parentIncomeEarnedBy"
        />
    )
}

/**
 * Returns a component for selecting the student's marital status options in a radio button format.
 * @returns {React.Component} - The RadioStudentMaritalStatus component.
 */
const RadioStudentMaritalStatus = () => {
    return(
        <Step3Radio
            headerText="Student's Marital Status"
            radioName="studentMaritalStatus"
            radioValues={
                [
                    {id: "student-marital-single", text: "Single"},
                    {id: "student-marital-married", text: "Married"}
                ]}
            fieldName="studentMaritalStatus"
        />
    )
}

/**
 * Represents a component for selecting if the student has dependents.
 * @returns {React.Component} - The RadioStudentHasDependents component.
 */
const RadioStudentHasDependents = () => {
    return(
        <Step3Radio
            headerText="Student has dependents"
            radioName="studentHasDependents"
            radioValues={
                [
                    {id: "student-dependents-yes", text: "Yes"},
                    {id: "student-dependents-no", text: "No"}
                ]}
            fieldName="studentHasDependents"
        />
    )
}

/**
 * Represents a component for selecting how many people in the independent student's family provide income.
 * @returns {React.Component} - The RadioStudentIncomeEarnedBy component.
 */
const RadioStudentIncomeEarnedBy = () => {
    return(
        <Step3Radio
            headerText="Student's Income Earned By"
            radioName="studentIncomeEarnedBy"
            radioValues={
                [
                    {id: "student-income-neither", text: "Neither"},
                    {id: "student-income-one", text: "One"},
                    {id: "student-income-both", text: "Both"}
                ]}
            fieldName="studentIncomeEarnedBy"
        />
    )
}

/**
 * Executes a thunk function that handles the next step click on Calculate in Step 3.
 * @returns {Function} The thunk function that can be dispatched.
 */
const Step3CalculateThunk = () => {
    return function (dispatch, getState) {

        let testData = getState().app.stepData.step3;
        let independent = getState().app.stepData.step1.status;
        let fullData = getState().app.stepData;
        let check = Step3Check(independent, testData, fullData);

        //  You shall pass
        if(Object.keys(check).length === 0) {
            dispatch(setCurrentStep(4));
        }
        //  You shall not pass!
        dispatch(setError(3, check));
    }
};

const detailInputCheck = (fullData) => {
    return {
        'familySize':  () => {
            let familySize = parseInt(fullData.step3.familySize);
            let testIndependent = parseInt(fullData.step1.status);

            if(!familySize || isNaN(familySize)) {
                return "Family size must be a valid number.";
            }

            if(testIndependent === STATUS_DEPENDENT) {
                let parentMaritalStatus = parseInt(fullData.step3.parentMaritalStatus);
                if(
                    parentMaritalStatus === PARENT_MARITAL_STATUS_SINGLE && familySize < 2) {
                    return "Family size must be 2 or more for dependent students with single parent.";
                }
                if(
                    parentMaritalStatus === PARENT_MARITAL_STATUS_MARRIED && familySize < 3) {
                    return "Family size must be 3 or more for dependent students with married parents.";
                }
                return "";
            }
            if(testIndependent === STATUS_INDEPENDENT) {
                let studentMaritalStatus = parseInt(fullData.step3.studentMaritalStatus);
                let hasDependents = parseInt(fullData.step3.studentHasDependents);

                if(studentMaritalStatus === STUDENT_MARITAL_STATUS_SINGLE) {
                    if(hasDependents === HAS_DEPENDENTS_YES && familySize < 2) {
                        return "Family size must be 2 or more for independent students that are single with dependents.";
                    }
                    if(hasDependents === HAS_DEPENDENTS_NO && familySize < 1) {
                        return "Family size must be 1 or more for independent students that are single with no dependents.";
                    }
                }
                if(studentMaritalStatus === STUDENT_MARITAL_STATUS_MARRIED) {
                    if(hasDependents === HAS_DEPENDENTS_YES && familySize < 3) {
                        return "Family size must be 3 or more for independent students that are married with dependents.";
                    }
                    if(hasDependents === HAS_DEPENDENTS_NO && familySize < 2) {
                        return "Family size must be 2 or more for independent students that are married with no dependents."
                    }
                    return "";
                }
                return "";

                return "Family size must be 1 or more for independent students.";
            }
            return "";
        },

    };

}

export default Step3;
